import type { CascadeOption } from '@byecode/ui'
import type {
    AnyObject,
    ArgumentsSchema,
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    FieldValue,
    FilterFormType,
    FilterGroupType,
    IndependentAction,
    InnerTypeADTValue,
    PageVariableSchema,
    SchemaProtocol,
    StateSchema,
    TableColumns,
    TypeInstanceMap
} from '@lighthouse/core'
import {
    type DataSourceAbstract,
    DataSourceType,
    type Field,
    type FieldADTValue,
    type FieldCellValue,
    type FieldType,
    ImageVariableValue,
    type VariableADTvalue,
    VariableType
} from '@lighthouse/core'
import { find, reduce } from 'rambda'

import { isEmptyRichTextValue } from '../components/RichText/helper'
import {
    defaultFormat,
    FieldIconTypeMap,
    FieldNameMap,
    FieldNameMapByInnerType,
    FilterWayToInnerTypeMap,
    innerTypeNameMap,
    innerTypeToFieldType,
    innerTypeToFieldTypeIconMap,
    innerTypeToFieldTypeMapInCondition,
    inputTypeToFieldTypeMap,
    inputTypeToInnerTypeMap,
    NO_EDIT_SYSTEM_FIELD,
    OUTPUT_ARGS_END_NODES,
    OUTPUT_FLOW_DATA_NODES,
    OUTPUT_SINGLE_ARGS_NODES,
    OUTPUT_SPECIFIC_DATA_NODES,
    OutputSpecificDataNodeList,
    SYSTEM_FIELD,
    SystemFieldIconTypeMap
} from '../constants'
import type { EndFlowNode, HandleSubProcessActionConfig } from '../types'
import { type DeptOption, EndNodeType, type FlowNode, type VariableTriggerConfig, type Workflow } from '../types'
import { getNodeDataSourceId } from './flow'
import { isArrayCellValue, isIdsValue, isNumberValue, isTextValue } from './helper'

export const getInputInnerTypeMatch = (innerType: TypeInstanceMap | undefined): TypeInstanceMap[] | undefined => {
    if (innerType === 'TEXT') {
        return ['TEXT', 'NUMBER', 'ARRAY', 'DATE', 'BOOL']
    }
    if (!innerType || innerType === 'NULL') {
        return undefined
    }
    return [innerType]
}

export const getOperatorRecordInnerTypeMatch = (
    innerType: TypeInstanceMap | undefined,
    fieldType?: FieldType
): TypeInstanceMap[] | undefined => {
    if (fieldType === 'file') {
        return ['TEXT', 'ARRAY']
    }
    if (innerType === 'TEXT') {
        return ['TEXT', 'NUMBER', 'ARRAY', 'DATE', 'BOOL']
    }
    if (innerType === 'ARRAY') {
        return ['TEXT', 'ARRAY']
    }
    if (!innerType || innerType === 'RICH_TEXT' || innerType === 'NULL') {
        return undefined
    }
    return [innerType]
}

export const getFieldIcon = (id: string, type: FieldType, innerType?: TypeInstanceMap, isFormula?: boolean) => {
    const list = id?.split('__') || ['']
    const fieldId = list[list.length - 1]
    if (SYSTEM_FIELD.has(fieldId)) {
        return SystemFieldIconTypeMap[fieldId]
    }
    if (type === 'aggregation') {
        return isFormula ? FieldIconTypeMap['formula'] : innerTypeToFieldTypeIconMap[innerType || 'NULL']
    }
    {
        return FieldIconTypeMap?.[type] || ''
    }
}

export const getFieldName = (type: FieldType, innerType: TypeInstanceMap | undefined, isFormula?: boolean) => {
    if (type === 'aggregation') {
        if (isFormula) {
            return FieldNameMap['formula']
        }
        return FieldNameMapByInnerType[innerType || 'NULL']
    }
    return FieldNameMap[type]
}

export const getRealFieldByInnerType: (field: Field, innerTypeMap: Record<TypeInstanceMap, FieldType | undefined>) => Field = (
    field,
    innerTypeMap
) => {
    if (field.type === 'formula') {
        const type = innerTypeMap[field.innerType]
        if (!type) {
            return field
        }

        return {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            [type]: field[type] || {}, // 需要保留如date类型的辅助字段 {date: {format: 'yyyy-MM-dd'}}
            id: field.id,
            type,
            dsId: field.dsId,
            innerType: field.innerType,
            name: field.name
        } as Field
    }
    return field
}

export const getRealField = (field: Field) => {
    return field
}

export const getIsPrimaryField = (field: Field, primaryDsId: string | undefined) => {
    return field.join?.joinDsId && field.join?.joinDsId === primaryDsId
}

export const getIsJoinField = (field: Field) => {
    return !!field.join?.joinDsId
}

export const getIsJoinNoEditSystemField = (field: Field) => {
    return field.join?.joinFieldId && NO_EDIT_SYSTEM_FIELD.has(field.join?.joinFieldId)
}

export function getBlockVariable(
    blockId: string,
    dataSourceList: DataSourceAbstract[],
    availableFieldBlocksInfo?: AvailableFieldBlockInfo[]
) {
    // const blockId = idVariable.inputVariable?.blockId || ''
    const fieldBlockInfo = find(item => item.block.id === blockId, availableFieldBlocksInfo || [])
    if (!fieldBlockInfo) {
        return
    }
    if (fieldBlockInfo.block.type === 'onOff') {
        return {
            id: '',
            type: 'checkbox',
            innerType: 'BOOL',
            name: ''
        } as Field
    }
    const {
        config: { inputType, fieldPointer }
    } = fieldBlockInfo.block
    const dsId = fieldBlockInfo.formContainer?.id
    const ds = find(item => item.id === dsId, dataSourceList)
    const field = ds?.schema[fieldPointer || '']
    if (!field) {
        return {
            id: '',
            type: inputTypeToFieldTypeMap[inputType],
            innerType: inputTypeToInnerTypeMap[inputType],
            name: ''
        } as Field
    }
    return field
}

export function getFilterVariableField(blockId: string, itemId: string, availableFilterBlocksInfo?: AvailableFilterBlockInfo[]) {
    const filterBlockInfo = find(item => item.block.id === blockId, availableFilterBlocksInfo || [])
    if (!filterBlockInfo) {
        return
    }
    const {
        block: {
            config: { filterItems }
        }
    } = filterBlockInfo
    // const ds = find(item => item.id === dsId, dataSourceList)
    const item = find(item => item.id === itemId, filterItems)
    if (!item) {
        return
    }
    const innerType = FilterWayToInnerTypeMap[item.filterWay]
    return {
        id: '',
        type: innerTypeToFieldTypeMapInCondition[innerType],
        innerType,
        name: ''
    } as Field
}

export function getIdVariableData(params: {
    idVariable: VariableADTvalue
    dataSourceList: DataSourceAbstract[]
    sourceFlowNode?: FlowNode[]
    dataSource?: DataSourceAbstract
    availableFieldBlocksInfo?: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo?: AvailableFilterBlockInfo[]
    viewDataSource?: DataSourceAbstract
    extraViewFields?: Field[]
    pageVariableSchemas?: PageVariableSchema[]
    pathParameter?: ArgumentsSchema[]
    queryParameter?: ArgumentsSchema[]
    stateParameter?: StateSchema[]
    workflowList?: Workflow[]
}): Field | undefined {
    const {
        idVariable,
        dataSourceList,
        sourceFlowNode = [],
        dataSource,
        viewDataSource,
        extraViewFields,
        availableFieldBlocksInfo,
        availableFilterBlocksInfo,
        pageVariableSchemas,
        pathParameter,
        queryParameter,
        stateParameter,
        workflowList
    } = params
    if (idVariable.type === VariableType.SYSTEM) {
        return undefined
    }

    if (idVariable.type === VariableType.INPUT) {
        return getBlockVariable(idVariable.inputVariable?.blockId || '', dataSourceList, availableFieldBlocksInfo)
    }

    if (idVariable.type === VariableType.FILTER) {
        return getFilterVariableField(
            idVariable.filterVariable?.blockId || '',
            idVariable.filterVariable?.itemId || '',
            availableFilterBlocksInfo
        )
    }

    if (idVariable.type === VariableType.USER) {
        const fieldId = idVariable.userVariable?.fieldId || ''
        const ds = find(item => item.id === 'USER_DATASOURCE', dataSourceList)
        const field = ds?.schema[fieldId]
        if (!field) {
            return undefined
        }
        return field
    }

    if (idVariable.type === VariableType.VIEW) {
        const fieldId = idVariable.viewVariable?.fieldId || ''
        const extraField = extraViewFields?.find(item => item.id === fieldId)
        if (extraField) {
            return extraField
        }
        const ds = find(item => item.id === viewDataSource?.id, dataSourceList)
        const field = ds?.schema[fieldId]
        if (!field) {
            return undefined
        }
        return field
    }

    if (idVariable.type === VariableType.VALUE) {
        if (idVariable.valueVariable?.value === '{currentUserId}') {
            return {
                id: '{currentUserId}',
                type: 'person',
                innerType: 'ARRAY'
            } as Field
        }
        return undefined
    }
    if (idVariable.type === VariableType.VARIABLE) {
        const dataSourceId = idVariable.fieldVariable?.dsId
        const fieldId = idVariable.fieldVariable?.fieldId || ''
        const ds = find(item => item.id === dataSourceId, dataSourceList)
        const field = ds?.schema[fieldId]
        if (!field) {
            return undefined
        }
        return field
    }

    if (idVariable.type === VariableType.ROUTE_PATH) {
        const id = idVariable.routePathVariable?.id
        const pathParam = pathParameter?.find(item => item.id === id)
        if (!pathParam) {
            return
        }
        return {
            id: idVariable.routePathVariable?.id,
            type: innerTypeToFieldTypeMapInCondition[pathParam.innerType],
            innerType: pathParam.innerType
        } as Field
    }

    if (idVariable.type === VariableType.ROUTE_QUERY) {
        const id = idVariable.routeQueryVariable?.id
        const queryParam = queryParameter?.find(item => item.id === id)
        if (!queryParam) {
            return
        }
        return {
            id: idVariable.routeQueryVariable?.id,
            type: innerTypeToFieldTypeMapInCondition[queryParam.innerType],
            innerType: queryParam.innerType
        } as Field
    }

    if (idVariable.type === VariableType.PAGE_DATA_QUERY) {
        const id = idVariable.pageDataQueryVariable?.id
        const itemId = idVariable.pageDataQueryVariable?.itemId
        const schema = pageVariableSchemas?.find(item => item.id === id)
        const variable = schema?.items.find(item => item.id === itemId)
        const fieldId = variable?.fieldId
        if (!fieldId) {
            return
        }
        const ds = dataSourceList.find(item => item.id === schema?.dsId)
        return ds?.schema?.[fieldId]
    }
    if (idVariable.type === VariableType.PAGE_STATE) {
        const stateId = idVariable.pageStateVariable?.id
        const state = stateParameter?.find(item => item.id === stateId)
        if (!state) {
            return
        }
        return {
            id: stateId,
            type: innerTypeToFieldTypeMapInCondition[state.innerType],
            innerType: state.innerType
        } as Field
    }
    if (idVariable.type === VariableType.FIELD_ID) {
        const fieldId = idVariable.fieldIdVariable?.fieldId || ''
        const field = dataSource?.schema[fieldId]
        if (!field) {
            return undefined
        }
        return field
    }

    if (idVariable.type === VariableType.ARG_VARIABLE) {
        const nodeId = idVariable.argVariable?.nodeId
        const argId = idVariable.argVariable?.argId || ''
        const node = find(item => item.id === nodeId, sourceFlowNode || [])
        if (!node) {
            return
        }
        if (!OUTPUT_SINGLE_ARGS_NODES.has(node.data.nodeType)) {
            return
        }
        if (OUTPUT_FLOW_DATA_NODES.has(node.data.nodeType)) {
            const handleSubProcessActionConfig = node.data.config as HandleSubProcessActionConfig
            const subProcessId = handleSubProcessActionConfig?.subProcessId
            const subProcess = find(item => item.id === subProcessId, workflowList || [])
            if (!subProcess) {
                return
            }
            const nodeList = subProcess.content?.nodes || []
            const endNode = find(item => item.data.nodeType === EndNodeType.END, nodeList) as EndFlowNode | undefined
            if (!endNode) {
                return
            }
            const outputArgs = endNode.data.config?.outputArgs || []
            const outputArg = find(item => item.id === argId, outputArgs)
            if (!outputArg) {
                return
            }
            return {
                id: outputArg.id,
                name: outputArg.name,
                type: innerTypeToFieldTypeMapInCondition[outputArg.innerType],
                innerType: outputArg.innerType
            } as Field
        }

        if (OUTPUT_ARGS_END_NODES.has(node.data.nodeType)) {
            const endNode = node as EndFlowNode
            const outputArgs = endNode.data.config?.outputArgs || []
            const outputArg = find(item => item.id === argId, outputArgs)
            if (!outputArg) {
                return
            }
            return {
                id: outputArg.id,
                name: outputArg.name,
                type: innerTypeToFieldTypeMapInCondition[outputArg.innerType],
                innerType: outputArg.innerType
            } as Field
        }

        const config = node.data.config as VariableTriggerConfig
        const { args } = config
        let arg: ArgumentsSchema | undefined

        if (args) {
            arg = find(item => item.id === argId, args)
        }

        if (OUTPUT_SPECIFIC_DATA_NODES.has(node.data.nodeType)) {
            const nodeType = node.data.nodeType as keyof typeof OutputSpecificDataNodeList
            arg = find(item => item.id === argId, OutputSpecificDataNodeList[nodeType])
        }

        if (!arg) {
            return
        }
        return {
            id: arg.id,
            name: arg.name,
            type: innerTypeToFieldTypeMapInCondition[arg.innerType],
            innerType: arg.innerType
        } as Field
    }

    if (idVariable.type === VariableType.UPSTREAM) {
        const nodeId = idVariable.upstreamVariable?.nodeId
        const fieldId = idVariable.upstreamVariable?.fieldId || ''
        const node = find(item => item.id === nodeId, sourceFlowNode || [])
        if (!node) {
            return
        }

        const dataSourceId = getNodeDataSourceId(node, sourceFlowNode)
        const ds = find(item => item.id === dataSourceId, dataSourceList)
        const field = ds?.schema[fieldId]
        if (!field) {
            return
        }
        return field
    }
    if (idVariable.type === VariableType.IMAGE) {
        if (idVariable.imageVariable?.dataKey === ImageVariableValue.IMAGE_INDEX) {
            return {
                id: '',
                type: 'number',
                innerType: 'NUMBER'
            } as Field
        }
        return {
            id: '',
            type: 'text',
            innerType: 'TEXT'
        } as Field
    }
    if (idVariable.type === VariableType.DEVICE_ID) {
        return {
            id: '',
            type: 'text',
            innerType: 'TEXT'
        } as Field
    }
}

export const isEmptyCellValue = function (cellValue: FieldADTValue): boolean {
    const { type, value, innerType } = cellValue ?? {}

    if (!type) {
        return true
    }
    switch (cellValue.type) {
        case 'number':
        case 'text':
        case 'url':
        case 'phoneNumber':
        case 'email':
        case 'id':
        case 'date': {
            return value === '' || value === undefined
        }
        case 'department':
        case 'parentDepartment':
        case 'singleSelect':
        case 'select':
        case 'person':
        case 'role':
        case 'video':
        case 'file': {
            return (cellValue?.value ?? []).length === 0
        }
        case 'notes': {
            return isEmptyRichTextValue(cellValue?.value)
        }
        case 'formula': {
            return isArrayCellValue(cellValue?.value) ? cellValue?.value.length === 0 : String(cellValue?.value).length === 0
        }
        case 'aggregation': {
            // 关联字段值合并之后，如果是数组则直接判断数组长度，否则按常规值处理
            if (Array.isArray(value)) {
                return value.length === 0
            }
            if (innerType === 'TEXT' || innerType === 'NUMBER') {
                return value === '' || value === undefined
            }
            return true
        }
        case 'checkbox': {
            return !value
        }
        default: {
            return true
        }
    }
}

export const getFieldValue = (type: FieldType, value: FieldCellValue): FieldValue => {
    switch (type) {
        case 'text':
        case 'url':
        case 'phoneNumber':
        case 'email':
        case 'id': {
            return {
                value: isTextValue(value) ? value : ''
            }
        }
        case 'number':
        case 'date': {
            return {
                value: isNumberValue(value) ? value : ''
            }
        }

        case 'select':
        case 'person': {
            return {
                value: isIdsValue(value) ? value : []
            }
        }
        case 'aggregation': {
            return { value }
        }

        default: {
            return {
                value: ''
            }
        }
    }
    // return { value }
}

export const getEmptyCellValue = function (type: FieldType): FieldCellValue {
    switch (type) {
        case 'number':
        case 'text':
        case 'url':
        case 'phoneNumber':
        case 'email':
        case 'id':
        case 'date': {
            return ''
        }
        case 'select':
        case 'person':
        case 'role':
        case 'video':
        case 'file': {
            return []
        }
        case 'aggregation':
        case 'notes': {
            return ''
        }
        default: {
            return ''
        }
    }
}

export const getAppointField = (dataSource: DataSourceAbstract, initFieldId: string) => {
    const { schema, viewOptions } = dataSource
    if (dataSource.type !== DataSourceType.joinDataSource) {
        return schema[initFieldId]
    }
    const primaryDsId = viewOptions.joinConfig?.primaryDsId
    const columns = Object.values(schema)
    return find(item => {
        const list = item.id.split('__')
        const fieldId = list[list.length - 1]
        return item.join?.joinDsId === primaryDsId && fieldId === initFieldId
    }, columns)
}

// 获取指定id字段是否在指定字段列表中， 注： 连接表的字段id会进行拼接 dsId-fieldId
export const getIsAppointField = (id: string | undefined, fieldSet: Set<string>) => {
    const list = id?.split('__') || []
    const fieldId = list[list.length - 1]
    return fieldSet.has(fieldId)
}

export const getRealFieldId = (id: string): string | undefined => {
    const list = id?.split('__') || [undefined]
    return list[list.length - 1]
}

export const getFieldActiveColor = (params: { field: Field; dataSource: DataSourceAbstract; dataSourceList: DataSourceAbstract[] }) => {
    const { field, dataSource, dataSourceList } = params
    const isSystemField = getIsAppointField(field.id, SYSTEM_FIELD)
    // const mainFieldIds = getPrimaryDataSourceEnableFieldIds(dataSource, dataSourceList)
    // if (isSystemField || mainFieldIds.has(field.id) || field.type === 'formula') {
    if (isSystemField || field.type === 'formula') {
        return 'var(--color-blue-500)'
    }

    return 'var(--color-gray-500)'
}

export const getFieldActiveCellColor = (params: { field: Field; dataSource: DataSourceAbstract; dataSourceList: DataSourceAbstract[] }) => {
    const { field, dataSource, dataSourceList } = params
    const isSystemField = getIsAppointField(field.id, SYSTEM_FIELD)
    // const mainFieldIds = getPrimaryDataSourceEnableFieldIds(dataSource, dataSourceList)
    // if (isSystemField || !mainFieldIds.has(field.id) || field.type === 'formula') {
    if (isSystemField || field.type === 'formula') {
        return 'var(--color-blue-50)'
    }

    return 'var(--color-gray-50)'
}

export interface FieldOption {
    label: string
    icon: string
    type: FieldType
    value: string
    innerType?: TypeInstanceMap
}

export const getFieldOptions = (schema?: SchemaProtocol['schema'], tableProps?: TableColumns, filter?: (field: Field) => boolean) => {
    if (!schema) {
        return []
    }
    const newFieldIds = Object.entries(schema).reduce(
        (preVal, [fieldId]) => (find(item => item === fieldId, preVal) ? preVal : [...preVal, fieldId]),
        [...(tableProps?.map(item => item.id) || [])]
    )
    return reduce<string, FieldOption[]>(
        (preVal, id) => {
            const field = schema[id]
            return field && (filter ? filter?.(field) : true)
                ? [
                      ...preVal,
                      {
                          label: field?.name,
                          value: id,
                          icon: getFieldIcon(field?.id, field?.type, field.innerType),
                          iconColor: 'var(--color-gray-400)',
                          type: field?.type,
                          innerType: field?.innerType
                      }
                  ]
                : preVal
        },
        [],
        newFieldIds
    )
}

export const getDepartmentToOptions = (list: DeptOption[]) => {
    return list.map(item => ({
        label: item.name,
        value: item.id,
        parentId: item.parentId,
        labelPath: item.labelPath
    }))
}

export const getDepartmentToTreeOptions = (list: DeptOption[]) => {
    // 创建一个id到对象的映射
    const map = new Map()
    const arr: CascadeOption[] = []
    // 初始化映射
    list.forEach(item => {
        map.set(item.id, {
            value: item.id,
            label: item.name,
            parentId: item.parentId || '',
            labelPath: item.labelPath
        })
    })
    // 构建树
    list.forEach(item => {
        const itemParentId = item.parentId
        if (itemParentId) {
            const parent = map.get(itemParentId)
            if (parent) {
                if (parent.children && parent.children.length > 0) {
                    parent.children.push(map.get(item.id))
                }
                if (!parent.children || parent.children.length === 0) {
                    {
                        const department = map.get(item.id)
                        parent.children = [map.get(item.id)]
                    }
                }
            }
            return
        }
        arr.push(map.get(item.id))
    })

    return arr
}

export const getCondition = (idVariable: VariableADTvalue, operator: string, paramList: VariableADTvalue[]): AnyObject => {
    return {
        idVariable,
        operator,
        paramList
    }
}

/**
 * 根据指定的字段 ID、逻辑关系和字段值生成一个单一的过滤条件。
 *
 * @param params - 参数对象
 * @param params.fieldId - 字段的唯一标识符
 * @param params.where - 条件的逻辑关系，默认为 'OR'
 * @param params.value - 字段的值
 * @returns 一个符合 FilterFormType 类型的过滤条件对象
 */
export const getSingleFilter = (params: { fieldId: string; where?: FilterGroupType['where']; value: FieldCellValue }): FilterFormType => {
    const { fieldId, where = 'OR', value } = params
    const condition = getCondition(
        {
            type: VariableType.FIELD_ID,
            fieldIdVariable: {
                fieldId
            }
        },
        '=',
        [
            {
                type: VariableType.VALUE,
                valueVariable: {
                    value
                }
            } as VariableADTvalue
        ]
    )

    const initFilter: FilterFormType = {
        expression: {
            conditions: [condition],
            where
        }
    }
    return initFilter
}

export const getInitInnerTypeADTValue = (innerType: TypeInstanceMap): InnerTypeADTValue => {
    switch (innerType) {
        case 'TEXT': {
            return {
                innerType: 'TEXT',
                value: ''
            }
        }
        case 'NUMBER': {
            return {
                innerType: 'NUMBER',
                value: Number.NaN
            }
        }
        case 'BOOL': {
            return {
                innerType: 'BOOL',
                value: false
            }
        }
        case 'DATE': {
            return {
                innerType: 'DATE',
                value: Number.NaN
            }
        }
        case 'ARRAY': {
            return {
                innerType: 'ARRAY',
                value: []
            }
        }
        // case 'RICH_TEXT': {
        //     return {
        //         innerType: 'RICH_TEXT',
        //         value: {}
        //     }
        // }
        default: {
            return {
                innerType: 'NULL',
                value: null
            }
        }
    }
}

export const getVirtualFieldByInnerType = (innerType: TypeInstanceMap): Field | undefined => {
    if (innerType === 'NULL') {
        return undefined
    }
    const extraConfig =
        innerType === 'DATE'
            ? {
                  date: {
                      format: defaultFormat
                  }
              }
            : {}
    return {
        id: '',
        name: innerTypeNameMap[innerType],
        type: innerTypeToFieldType[innerType],
        dsId: '',
        innerType,
        ...extraConfig
    } as Field
}
